/**
 * Hooks de TanStack Query para OT (Orden de Trabajo).
 *
 * Las mutaciones de OT también invalidan las queries de PR/PO porque la OT
 * cuenta en consumoCoste y puede afectar visualizaciones de saldos.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { buildRepos } from "@/core/data/repos";
import { validarReprogramacion, inconsistenciasLineaBase } from "./soRules";
import { ValidationError } from "@core/data/repos/BaseRepo";
import { registrarAuditoria } from "@/core/audit/registrarAuditoria";
import { detectarSosVencidas } from "./soRules";
import { notificarTransicionSo } from "@core/mail/cableado";
import type { So, Tarea, VeredictoInt } from "@app-types/entities";

function invalidateAfterSoMutation(
  qc: ReturnType<typeof useQueryClient>,
  tenantId: string,
  soId?: string,
) {
  if (soId) void qc.invalidateQueries({ queryKey: ["so", tenantId, soId] });
  void qc.invalidateQueries({ queryKey: ["sos", tenantId] });
  // Cross-module: OT afecta consumoCoste y vistas de PR/PO/AFE
  void qc.invalidateQueries({ queryKey: ["prs", tenantId] });
  void qc.invalidateQueries({ queryKey: ["pos", tenantId] });
}

export function useSos(tenantId: string) {
  return useQuery({
    queryKey: ["sos", tenantId],
    queryFn: () => buildRepos(tenantId).so.list(),
    staleTime: 30_000,
  });
}

export function useSosActive(tenantId: string) {
  return useQuery({
    queryKey: ["sos", tenantId, "active"],
    queryFn: () => buildRepos(tenantId).so.listActive(),
    staleTime: 30_000,
  });
}

/**
 * Siguiente código de OT consecutivo (OT-#### de 4 dígitos). Se asigna solo,
 * no se digita. La key comparte prefijo ["sos", tenantId] → se invalida
 * automáticamente al crear/cambiar una OT. staleTime 0 = se recalcula al abrir
 * el formulario, para no repetir un consecutivo.
 */
export function useNextSoCode(tenantId: string) {
  return useQuery({
    queryKey: ["sos", tenantId, "nextCode"],
    queryFn: () => buildRepos(tenantId).so.getNextSoCode(),
    staleTime: 0,
  });
}

export function useSo(tenantId: string, id: string | null) {
  return useQuery({
    queryKey: ["so", tenantId, id],
    queryFn: () => (id ? buildRepos(tenantId).so.get(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

export function useTareasSo(tenantId: string, soId: string | null) {
  return useQuery({
    queryKey: ["tareas", tenantId, soId],
    queryFn: () => (soId ? buildRepos(tenantId).so.listTareas(soId) : Promise.resolve([])),
    enabled: !!soId,
  });
}

/**
 * Todas las tareas del tenant (para EV físico agregado a nivel AFE/Budget).
 * Se invalida junto con las mutaciones de tareas ("tareas" prefijo).
 */
export function useAllTareas(tenantId: string) {
  return useQuery({
    queryKey: ["tareas", tenantId, "__all__"],
    queryFn: () => buildRepos(tenantId).so.listAllTareas(),
    staleTime: 30_000,
  });
}

export function useCreateSo(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<So, "id" | "createdAt" | "updatedAt" | "version">) =>
      buildRepos(tenantId).so.create(data),
    onSuccess: () => invalidateAfterSoMutation(qc, tenantId),
  });
}

export function useUpdateSo(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, version }: { id: string; data: Partial<So>; version: number }) =>
      buildRepos(tenantId).so.update(id, data, version),
    onSuccess: (_, { id }) => invalidateAfterSoMutation(qc, tenantId, id),
  });
}

/**
 * Elimina una OT ARRASTRANDO todo lo suyo: eventos, reportes diarios (con sus
 * fotos, que viven dentro del propio reporte), tareas y por último la OT.
 *
 * `so.delete(id)` solo quitaba la fila de la OT. Lo demás quedaba en la base
 * sin nada de qué colgar: invisible en la interfaz, imposible de limpiar desde
 * la app y contando en cualquier consulta que barra por tipo. En producción
 * quedaron 19 filas así.
 *
 * ORDEN: de las hojas hacia la raíz, y la OT de última. Si algo falla a mitad,
 * queda una OT viva a la que le faltan partes —visible y corregible— en vez de
 * registros colgando de un id que ya no existe, que es justo el estado que
 * nadie detecta. Por eso NO se borra la OT primero aunque sea más rápido.
 *
 * El aviso que enumera lo que se va lo arma `impactoEliminarSo` y se confirma
 * antes de llamar aquí.
 */
export function useDeleteSo(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const repos = buildRepos(tenantId);

      const eventos = await repos.evento.listBySo(id);
      for (const e of eventos) await repos.evento.delete(e.id);

      // Las fotos van embebidas en el payload del reporte (data-URL), así que
      // borrar el reporte se las lleva. No hay blobs sueltos que recoger.
      const rds = await repos.rd.listBySo(id);
      for (const r of rds) await repos.rd.delete(r.id);

      const tareas = await repos.so.listTareas(id);
      for (const t of tareas) await repos.so.deleteTarea(t.id);

      await repos.so.delete(id);
    },
    onSuccess: (_, id) => {
      invalidateAfterSoMutation(qc, tenantId, id);
      // La OT ya no existe: lo que colgaba de ella tiene que salir de la caché
      // o la interfaz seguiría mostrando reportes de una OT borrada.
      void qc.invalidateQueries({ queryKey: ["rds"] });
      void qc.invalidateQueries({ queryKey: ["eventos"] });
    },
  });
}

/**
 * Cambios de sub-flags (nivelRevision, enReprogramacion) sin cambio de
 * estado. Misma invalidación que cambio de estado.
 */
export function useUpdateSoFlags(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      flags,
      userEmail,
    }: {
      id: string;
      flags: {
        nivelRevision?: "PL2" | "PL" | null;
        enReprogramacion?: boolean;
        conceptoInt?: string;
        aprobadoPorInt?: string;
        veredictoInt?: VeredictoInt;
        fechaConceptoInt?: string;
        montoConceptoInt?: number;
      };
      userEmail: string;
    }) => {
      await buildRepos(tenantId).so.updateSubFlags(id, flags, userEmail);
      void registrarAuditoria(tenantId, {
        usuario: userEmail,
        // El concepto de Interventoría se audita como acto propio, no como un
        // cambio de nivel: es un juicio técnico con consecuencias.
        accion:
          flags.veredictoInt !== undefined || flags.conceptoInt !== undefined
            ? "SO_CONCEPTO_INT"
            : "SO_NIVEL_REVISION",
        entidad: "SO",
        entidadId: id,
        diff: { ...flags },
      });
    },
    onSuccess: (_, { id }) => invalidateAfterSoMutation(qc, tenantId, id),
  });
}

/** Aceptación formal de la OT por el contratista (no cambia el estado). */
export function useRegistrarAceptacionSo(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      userEmail,
      observaciones,
    }: {
      id: string;
      userEmail: string;
      observaciones?: string;
    }) => {
      await buildRepos(tenantId).so.registrarAceptacion(id, userEmail, observaciones);
      void registrarAuditoria(tenantId, {
        usuario: userEmail,
        accion: "SO_ACEPTADA_CONTRATISTA",
        entidad: "SO",
        entidadId: id,
        ...(observaciones ? { diff: { observaciones } } : {}),
      });
    },
    onSuccess: (_, { id }) => invalidateAfterSoMutation(qc, tenantId, id),
  });
}

export function useChangeSoState(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      nextState,
      userEmail,
      userName,
      observaciones,
    }: {
      id: string;
      nextState: So["estado"];
      userEmail: string;
      userName?: string;
      observaciones?: string;
    }) => {
      const repos = buildRepos(tenantId);
      const repo = repos.so;
      const antes = await repo.get(id);
      const estadoAnterior = antes?.estado;

      // R3 — el PL2 no puede APROBAR una línea base que contradiga el trabajo
      // ya ejecutado. Es aquí, y no al guardar, porque el contratista sí puede
      // dejar el parcial a medio ajustar mientras reprograma.
      if (nextState === "APROBADA") {
        const [tareas, rds] = await Promise.all([repo.listTareas(id), repos.rd.listBySo(id)]);
        const malas = inconsistenciasLineaBase(tareas, rds);
        if (malas.length > 0) {
          throw new ValidationError(
            "No se puede aprobar esta línea base: hay tareas planeadas por debajo de lo ya ejecutado.\n" +
              malas.map((p) => `• ${p.mensaje}`).join("\n"),
          );
        }
      }

      await repo.changeState(id, nextState, userEmail, observaciones);
      const despues = await repo.get(id);
      if (despues && estadoAnterior) {
        void registrarAuditoria(tenantId, {
          usuario: userEmail,
          accion: `SO_${nextState}`,
          entidad: "SO",
          entidadId: despues.codigo ?? id,
          diff: {
            estadoAnterior,
            estadoNuevo: nextState,
            ...(observaciones ? { motivo: observaciones } : {}),
          },
        });
        void notificarTransicionSo({
          tenantId,
          so: despues,
          estadoAnterior,
          estadoNuevo: nextState,
          actor: { email: userEmail, ...(userName !== undefined && { nombre: userName }) },
          ...(observaciones !== undefined && { motivo: observaciones }),
        });
      }
    },
    onSuccess: (_, { id }) => invalidateAfterSoMutation(qc, tenantId, id),
  });
}

/**
 * Hook que detecta OTs vencidas (plazo cumplido sin cierre) y muestra
 * advertencia operativa. No promueve estado automáticamente — la OT se
 * mantiene APROBADA pero queda flagada para revisión.
 *
 * Devuelve la lista de OTs vencidas para que el llamador pueda mostrarla.
 */
export function useDetectarSosVencidas(sos: So[] | undefined): So[] {
  const memo = useRef<So[]>([]);
  useEffect(() => {
    if (!sos) return;
    memo.current = detectarSosVencidas(sos);
  }, [sos]);
  return sos ? detectarSosVencidas(sos) : memo.current;
}

export function useCreateTarea(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      soId,
      data,
    }: {
      soId: string;
      data: Omit<Tarea, "id" | "soId" | "createdAt" | "updatedAt" | "version">;
    }) => buildRepos(tenantId).so.createTarea(soId, data),
    onSuccess: (_, { soId }) => {
      void qc.invalidateQueries({ queryKey: ["tareas", tenantId] });
      invalidateAfterSoMutation(qc, tenantId, soId);
    },
  });
}

export function useUpdateTarea(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tareaId,
      data,
    }: {
      tareaId: string;
      soId: string;
      data: Partial<Tarea>;
    }) => buildRepos(tenantId).so.updateTarea(tareaId, data),
    onSuccess: (_, { soId }) => {
      void qc.invalidateQueries({ queryKey: ["tareas", tenantId] });
      invalidateAfterSoMutation(qc, tenantId, soId);
    },
  });
}

export function useDeleteTarea(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tareaId }: { tareaId: string; soId: string }) =>
      buildRepos(tenantId).so.deleteTarea(tareaId),
    onSuccess: (_, { soId }) => {
      void qc.invalidateQueries({ queryKey: ["tareas", tenantId] });
      invalidateAfterSoMutation(qc, tenantId, soId);
    },
  });
}

/**
 * Purga una tarea cuya OT ya no existe — limpieza del ADMIN_PMIS.
 *
 * No invalida la cache de la OT porque no hay OT que refrescar; solo la lista
 * global de tareas, que es de donde el panel de huerfanos las lee.
 */
export function usePurgarTareaHuerfana(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tareaId: string) => buildRepos(tenantId).so.purgarTareaHuerfana(tareaId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tareas", tenantId] });
    },
  });
}

export function useReplaceTareas(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      soId,
      tareas,
    }: {
      soId: string;
      tareas: Omit<Tarea, "tenantId" | "createdAt" | "updatedAt" | "version">[];
    }) => {
      const repos = buildRepos(tenantId);
      // R1 y R3 — invariante de datos, no de interfaz.
      //
      // `replaceTareas` borra las tareas que ya no vienen en la lista SIN
      // mirar si tienen avance. Como el valor ganado cruza
      // `avancesTareas.tareaId` contra la lista de tareas, esos avances dejan
      // de sumar al EV SIN NINGÚN AVISO y la curva S se degrada en silencio.
      //
      // La comprobación vive aquí, sobre los tres adaptadores, porque es el
      // único punto de convergencia que puede leer los reportes de la OT:
      // `SoRepo` no tiene acceso a la entidad RD.
      const [existentes, rds] = await Promise.all([
        repos.so.listTareas(soId),
        repos.rd.listBySo(soId),
      ]);
      // Al GUARDAR solo se impide lo DESTRUCTIVO (R1): borrar una tarea que ya
      // tiene avance deja los reportes apuntando a algo inexistente y no se
      // puede deshacer.
      //
      // R3 —cantidad por debajo de lo ejecutado— NO se impide aquí: el
      // contratista puede guardar el parcial y seguir trabajando con la
      // inconsistencia señalada. Lo que se bloquea es que el PL2 REAPRUEBE la
      // línea base mientras exista (ver `useChangeSoState`).
      const { problemas } = validarReprogramacion(tareas, existentes, rds);
      const destructivos = problemas.filter((p) => p.regla === "R1");
      if (destructivos.length > 0) {
        throw new ValidationError(
          "No se puede reprogramar así:\n" + destructivos.map((p) => `• ${p.mensaje}`).join("\n"),
        );
      }
      return repos.so.replaceTareas(soId, tareas);
    },
    onSuccess: (_, { soId }) => {
      void qc.invalidateQueries({ queryKey: ["tareas", tenantId] });
      invalidateAfterSoMutation(qc, tenantId, soId);
    },
  });
}
